import {
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Flex,
  FormControl,
  FormErrorMessage,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Skeleton,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import React, { useState, useMemo } from 'react';
import { IconType } from 'react-icons';
import { FiExternalLink, FiDownload, FiCalendar, FiX, FiAlertCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

interface ReportCardWithDateFiltersProps {
  title: string;
  description: string;
  icon: IconType;
  iconColor?: string;
  statValue?: string | number;
  statLabel?: string;
  statHelpText?: string;
  viewLink?: string;
  onExport: (filters: { startDate?: string; endDate?: string }) => Promise<void>;
  isLoading?: boolean;
  isExporting?: boolean;
}

const ReportCardWithDateFilters: React.FC<ReportCardWithDateFiltersProps> = ({
  title,
  description,
  icon,
  iconColor = 'blue.500',
  statValue,
  statLabel,
  statHelpText,
  viewLink,
  onExport,
  isLoading = false,
  isExporting = false,
}) => {
  const navigate = useNavigate();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const iconBgColor = useColorModeValue('gray.50', 'gray.700');
  const inputBgColor = useColorModeValue('white', 'gray.700');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const handleViewClick = () => {
    if (viewLink) {
      navigate(viewLink);
    }
  };

  const handleExport = async () => {
    await onExport({
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    });
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
  };

  const hasFilters = startDate || endDate;

  // Validação: data inicial não pode ser maior que data final
  const isDateRangeValid = useMemo(() => {
    if (!startDate || !endDate) return true; // Se uma das datas não estiver preenchida, não valida
    return new Date(startDate) <= new Date(endDate);
  }, [startDate, endDate]);

  const canExport = hasFilters ? isDateRangeValid : true;

  return (
    <Card
      bg={bgColor}
      borderColor={borderColor}
      borderWidth="1px"
      borderRadius="lg"
      overflow="hidden"
      transition="all 0.2s"
      _hover={{
        transform: 'translateY(-4px)',
        shadow: 'lg',
      }}
    >
      <CardHeader pb={2}>
        <Flex justify="space-between" align="center">
          <HStack spacing={3}>
            <Flex
              w="48px"
              h="48px"
              bg={iconBgColor}
              borderRadius="md"
              align="center"
              justify="center"
            >
              <Icon as={icon} boxSize={6} color={iconColor} />
            </Flex>
            <Box>
              <Heading size="md" mb={1}>
                {title}
              </Heading>
              <Text fontSize="sm" color="gray.500">
                {description}
              </Text>
            </Box>
          </HStack>
        </Flex>
      </CardHeader>

      <CardBody py={4}>
        {isLoading ? (
          <Skeleton height="80px" borderRadius="md" mb={4} />
        ) : (
          statValue !== undefined && (
            <Stat mb={4}>
              <StatLabel fontSize="sm" color="gray.500">
                {statLabel}
              </StatLabel>
              <StatNumber fontSize="3xl" fontWeight="bold">
                {statValue}
              </StatNumber>
              {statHelpText && (
                <StatHelpText fontSize="sm">{statHelpText}</StatHelpText>
              )}
            </Stat>
          )
        )}

        {/* Filtros de Data */}
        <VStack spacing={3} align="stretch">
          <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase">
            Filtrar por Período
          </Text>

          <FormControl isInvalid={!isDateRangeValid}>
            <InputGroup size="sm">
              <InputLeftElement>
                <Icon as={FiCalendar} color="gray.400" />
              </InputLeftElement>
              <Input
                type="date"
                placeholder="Data início"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                bg={inputBgColor}
                fontSize="sm"
              />
            </InputGroup>
          </FormControl>

          <FormControl isInvalid={!isDateRangeValid}>
            <InputGroup size="sm">
              <InputLeftElement>
                <Icon as={FiCalendar} color="gray.400" />
              </InputLeftElement>
              <Input
                type="date"
                placeholder="Data fim"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                bg={inputBgColor}
                fontSize="sm"
              />
            </InputGroup>
            {!isDateRangeValid && (
              <FormErrorMessage fontSize="xs" mt={1}>
                <Icon as={FiAlertCircle} mr={1} />
                Data inicial não pode ser maior que a final
              </FormErrorMessage>
            )}
          </FormControl>

          <Button
            w="full"
            variant="ghost"
            size="sm"
            leftIcon={<FiX />}
            onClick={handleClearFilters}
            isDisabled={!hasFilters || isLoading || isExporting}
            opacity={hasFilters ? 1 : 0.5}
          >
            Limpar Filtros
          </Button>
        </VStack>
      </CardBody>

      <CardFooter pt={0}>
        <VStack w="full" spacing={2}>
          {viewLink && (
            <Button
              w="full"
              colorScheme="blue"
              rightIcon={<FiExternalLink />}
              onClick={handleViewClick}
              isDisabled={isLoading}
            >
              Ver Detalhes
            </Button>
          )}
          <Button
            w="full"
            variant="outline"
            colorScheme="green"
            rightIcon={<FiDownload />}
            onClick={handleExport}
            isLoading={isExporting}
            isDisabled={isLoading || !canExport}
          >
            Exportar Dados
          </Button>
        </VStack>
      </CardFooter>
    </Card>
  );
};

export default ReportCardWithDateFilters;
